import React, { useState } from 'react';
import Layout from '../../components/layout/Layout';
import api from '../../services/api';
import * as XLSX from 'xlsx';
import { Upload, Download, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';

const BulkImport = () => {
  const [step, setStep] = useState('upload'); // upload, preview, importing, done
  const [rawData, setRawData] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState({ success: 0, failed: 0, errors: [] });
  const [districts, setDistricts] = useState([]);

  // Download template
  const downloadTemplate = () => {
    const template = [
      {
        'Institution Name': 'Govt Vocational Training Institute Example',
        'Address': 'Plot 1, Street 1, City',
        'District Name': 'Karachi East',
        'Sector': 'Public',
        'Focal Person Name': 'Ahmed Khan',
        'Focal Person Contact': '03001234567',
        'Latitude': '24.860700',
        'Longitude': '67.001100',
        'Geo-fence Radius': '150',
        'Procurement Year': '2026',
        'Contract Start Date': '2026-01-01',
        'Contract End Date': '2026-12-31',
        'Course 1 Name': 'Electrician',
        'Course 1 Trainees': '30',
        'Course 1 Start Date': '2026-01-05',
        'Course 1 End Date': '2026-07-05',
        'Course 2 Name': 'Plumber',
        'Course 2 Trainees': '25',
        'Course 2 Start Date': '2026-01-05',
        'Course 2 End Date': '2026-07-05',
        'Course 3 Name': '',
        'Course 3 Trainees': '',
        'Course 3 Start Date': '',
        'Course 3 End Date': '',
      }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Institutions');

    // Set column widths
    ws['!cols'] = [
      { wch: 40 }, { wch: 30 }, { wch: 20 }, { wch: 10 },
      { wch: 20 }, { wch: 18 }, { wch: 12 }, { wch: 12 },
      { wch: 15 }, { wch: 15 }, { wch: 18 }, { wch: 18 },
      { wch: 20 }, { wch: 15 }, { wch: 18 }, { wch: 18 },
      { wch: 20 }, { wch: 15 }, { wch: 18 }, { wch: 18 },
      { wch: 20 }, { wch: 15 }, { wch: 18 }, { wch: 18 },
    ];

    XLSX.writeFile(wb, 'FMAS_Institution_Import_Template.xlsx');
    toast.success('Template downloaded!');
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    try {
      // Fetch districts for validation
      const distResponse = await api.get('/districts');
      const districtList = distResponse.data.districts;
      setDistricts(districtList);

      const reader = new FileReader();
      reader.onload = (evt) => {
        const data = evt.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          toast.error('Excel file is empty!');
          return;
        }

        // Validate and process data
        const processed = [];
        const validationErrors = [];

        jsonData.forEach((row, index) => {
          const rowNum = index + 2;
          const rowErrors = [];

          // Required fields
          if (!row['Institution Name']) rowErrors.push('Institution Name is required');
          if (!row['Sector']) rowErrors.push('Sector is required');
          if (!row['Latitude']) rowErrors.push('Latitude is required');
          if (!row['Longitude']) rowErrors.push('Longitude is required');
          if (!['Public', 'Private'].includes(row['Sector'])) {
            rowErrors.push('Sector must be Public or Private');
          }

          // Find district
          let districtId = null;
          if (row['District Name']) {
            const district = districtList.find(d =>
              d.district_name.toLowerCase() === row['District Name'].toLowerCase()
            );
            if (district) {
              districtId = district.id;
            } else {
              rowErrors.push(`District "${row['District Name']}" not found`);
            }
          }

          // Build courses array
          const courses = [];
          for (let i = 1; i <= 5; i++) {
            if (row[`Course ${i} Name`]) {
              courses.push({
                course_name: row[`Course ${i} Name`],
                number_of_trainees: parseInt(row[`Course ${i} Trainees`]) || 0,
                start_date: row[`Course ${i} Start Date`] || null,
                end_date: row[`Course ${i} End Date`] || null,
              });
            }
          }

          const processedRow = {
            rowNum,
            name: row['Institution Name'],
            address: row['Address'] || '',
            district_id: districtId,
            district_name: row['District Name'] || '',
            training_sector: row['Sector'],
            focal_person_name: row['Focal Person Name'] || '',
            focal_person_contact: String(row['Focal Person Contact'] || ''),
            latitude: parseFloat(row['Latitude']) || 0,
            longitude: parseFloat(row['Longitude']) || 0,
            geo_fence_radius: parseInt(row['Geo-fence Radius']) || 150,
            procurement_year: parseInt(row['Procurement Year']) || new Date().getFullYear(),
            contract_start_date: row['Contract Start Date'] || null,
            contract_end_date: row['Contract End Date'] || null,
            courses,
            errors: rowErrors,
            status: rowErrors.length === 0 ? 'valid' : 'error'
          };

          processed.push(processedRow);
          if (rowErrors.length > 0) {
            validationErrors.push({ row: rowNum, errors: rowErrors });
          }
        });

        setPreviewData(processed);
        setErrors(validationErrors);
        setStep('preview');
      };
      reader.readAsBinaryString(file);
    } catch (err) {
      toast.error('Failed to read file');
      console.error(err);
    }
  };

  // Import data
  const handleImport = async () => {
    const validRows = previewData.filter(row => row.status === 'valid');

    if (validRows.length === 0) {
      toast.error('No valid rows to import!');
      return;
    }

    setImporting(true);
    setStep('importing');

    let successCount = 0;
    let failedCount = 0;
    const importErrors = [];

    for (const row of validRows) {
      try {
        await api.post('/institutions', {
          name: row.name,
          address: row.address,
          latitude: row.latitude,
          longitude: row.longitude,
          geo_fence_radius: row.geo_fence_radius,
          focal_person_name: row.focal_person_name,
          focal_person_contact: row.focal_person_contact,
          training_sector: row.training_sector,
          district_id: row.district_id,
          procurement_year: row.procurement_year,
          contract_start_date: row.contract_start_date,
          contract_end_date: row.contract_end_date,
          courses: row.courses,
        });
        successCount++;
      } catch (err) {
        failedCount++;
        importErrors.push({
          name: row.name,
          error: err.response?.data?.message || 'Failed to import'
        });
      }
    }

    setResults({ success: successCount, failed: failedCount, errors: importErrors });
    setImporting(false);
    setStep('done');
  };

  const validCount = previewData.filter(r => r.status === 'valid').length;
  const errorCount = previewData.filter(r => r.status === 'error').length;

  return (
    <Layout title="Bulk Import Institutions">

      {/* Progress Steps */}
      <div className="flex items-center mb-8">
        {['upload', 'preview', 'importing', 'done'].map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
              ${step === s ? 'bg-blue-800 text-white' : 
                ['preview', 'importing', 'done'].indexOf(s) < ['preview', 'importing', 'done'].indexOf(step)
                  ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
              <span>{i + 1}</span>
              <span className="capitalize">{s}</span>
            </div>
            {i < 3 && <div className="flex-1 h-1 bg-gray-200 mx-2" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Upload */}
      {step === 'upload' && (
        <div className="space-y-6">
          {/* Download Template */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h2 className="text-lg font-bold text-blue-800 mb-2">
              Step 1: Download Template
            </h2>
            <p className="text-blue-600 text-sm mb-4">
              Download the Excel template, fill in your institution data, then upload it below.
            </p>
            <button
              onClick={downloadTemplate}
              className="flex items-center gap-2 bg-blue-800 text-white px-4 py-2 rounded-lg hover:bg-blue-900 text-sm"
            >
              <Download size={16} />
              Download Excel Template
            </button>
          </div>

          {/* Template Instructions */}
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="font-bold text-gray-800 mb-3">Template Instructions:</h3>
            <div className="text-sm text-gray-600 space-y-2">
              <p>✅ <strong>Institution Name</strong> — Full official name (required)</p>
              <p>✅ <strong>Address</strong> — Complete address</p>
              <p>✅ <strong>District Name</strong> — Must match exactly (e.g. Karachi East, Jamshoro)</p>
              <p>✅ <strong>Sector</strong> — Must be exactly <strong>Public</strong> or <strong>Private</strong></p>
              <p>✅ <strong>Latitude/Longitude</strong> — GPS coordinates (required)</p>
              <p>✅ <strong>Geo-fence Radius</strong> — In meters (default 150)</p>
              <p>✅ <strong>Courses</strong> — Up to 5 courses per institution</p>
              <p>⚠️ <strong>Date format</strong> — Use YYYY-MM-DD (e.g. 2026-01-05)</p>
            </div>
          </div>

          {/* Upload File */}
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="font-bold text-gray-800 mb-4">Step 2: Upload Filled Excel File</h3>
            <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition">
              <Upload size={32} className="text-gray-400 mb-3" />
              <p className="text-gray-600 font-medium">Click to upload Excel file</p>
              <p className="text-gray-400 text-sm mt-1">.xlsx or .xls files only</p>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>
          </div>
        </div>
      )}

      {/* Step 2: Preview */}
      {step === 'preview' && (
        <div className="space-y-6">

          {/* Summary */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-blue-800">{previewData.length}</p>
              <p className="text-blue-600 text-sm">Total Rows</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-green-700">{validCount}</p>
              <p className="text-green-600 text-sm">Valid — Ready to Import</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-red-700">{errorCount}</p>
              <p className="text-red-600 text-sm">Errors — Will be skipped</p>
            </div>
          </div>

          {/* Errors */}
          {errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <h3 className="font-bold text-red-700 mb-3 flex items-center gap-2">
                <AlertTriangle size={18} />
                Validation Errors (these rows will be skipped)
              </h3>
              <div className="space-y-2">
                {errors.map((err, i) => (
                  <div key={i} className="text-sm">
                    <span className="font-medium text-red-600">Row {err.row}:</span>
                    <span className="text-red-500 ml-2">{err.errors.join(', ')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Preview Table */}
          <div className="bg-white rounded-xl shadow">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-bold text-gray-800">Data Preview</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Row</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Institution Name</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">District</th>
                    <th className="text-left px-4 py-3 text-gray-600 font-semibold">Sector</th>
                    <th className="text-center px-4 py-3 text-gray-600 font-semibold">Courses</th>
                    <th className="text-center px-4 py-3 text-gray-600 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((row, index) => (
                    <tr key={index} className={`border-t border-gray-100 
                      ${row.status === 'error' ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                      <td className="px-4 py-3 text-gray-500">{row.rowNum}</td>
                      <td className="px-4 py-3 font-medium text-gray-800">
                        {row.name}
                        {row.status === 'error' && (
                          <div className="text-xs text-red-500 mt-1">
                            {row.errors.join(', ')}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{row.district_name || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium
                          ${row.training_sector === 'Public'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-purple-100 text-purple-700'}`}>
                          {row.training_sector}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-gray-600">
                        {row.courses.length}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {row.status === 'valid' ? (
                          <span className="flex items-center justify-center gap-1 text-green-600 text-xs">
                            <CheckCircle size={14} /> Ready
                          </span>
                        ) : (
                          <span className="flex items-center justify-center gap-1 text-red-500 text-xs">
                            <XCircle size={14} /> Error
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={() => setStep('upload')}
              className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50"
            >
              ← Upload Different File
            </button>
            <button
              onClick={handleImport}
              disabled={validCount === 0}
              className={`flex-1 py-3 rounded-lg text-white font-semibold
                ${validCount > 0
                  ? 'bg-blue-800 hover:bg-blue-900'
                  : 'bg-gray-300 cursor-not-allowed'}`}
            >
              Import {validCount} Institutions →
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Importing */}
      {step === 'importing' && (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-800 mb-6"></div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Importing institutions...</h2>
          <p className="text-gray-500">Please wait. Do not close this page.</p>
        </div>
      )}

      {/* Step 4: Done */}
      {step === 'done' && (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
            <CheckCircle size={48} className="text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-green-800 mb-2">Import Complete!</h2>
            <p className="text-green-600">
              Successfully imported {results.success} institutions
            </p>
            {results.failed > 0 && (
              <p className="text-red-500 mt-2">
                {results.failed} institutions failed to import
              </p>
            )}
          </div>

          {results.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <h3 className="font-bold text-red-700 mb-3">Failed Imports:</h3>
              {results.errors.map((err, i) => (
                <div key={i} className="text-sm text-red-600 mb-1">
                  <strong>{err.name}:</strong> {err.error}
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={() => {
                setStep('upload');
                setPreviewData([]);
                setErrors([]);
                setResults({ success: 0, failed: 0, errors: [] });
              }}
              className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50"
            >
              Import More
            </button>
            <button
              onClick={() => window.location.href = '/institutions'}
              className="flex-1 bg-blue-800 text-white py-3 rounded-lg hover:bg-blue-900"
            >
              View Institutions →
            </button>
          </div>
        </div>
      )}

    </Layout>
  );
};

export default BulkImport;